
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Plus, ArrowRight, ShoppingCart, CalendarClock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useThemeConfig } from '@/components/ui/theme-config-provider';
import { UpcomingRecurringProduct } from '@/services/recurring-products.service';

interface UpcomingProductsSectionProps {
  upcomingProducts: UpcomingRecurringProduct[];
  isLoading: boolean;
  onAddToList: (productId: string) => Promise<void>;
}

const UpcomingProductsSection: React.FC<UpcomingProductsSectionProps> = ({
  upcomingProducts,
  isLoading,
  onAddToList
}) => {
  const navigate = useNavigate();
  const { theme } = useThemeConfig();

  if (isLoading) {
    return (
      <Card
        className="p-4 animate-pulse"
        style={{ backgroundColor: theme.cardBackgroundColor || '#ffffff' }}
      >
        <div
          className="h-6 rounded w-1/4 mb-4"
          style={{ backgroundColor: theme.borderColor }}
        ></div>
        <div className="space-y-2">
          <div className="h-20 rounded" style={{ backgroundColor: theme.cardBackgroundColor }}></div>
          <div className="h-20 rounded" style={{ backgroundColor: theme.cardBackgroundColor }}></div>
        </div>
      </Card>
    );
  }

  if (upcomingProducts.length === 0) {
    return (
      <Card className="p-4" style={{ backgroundColor: theme.cardBackgroundColor || '#ffffff',
        color: theme.sectionTitleColor || '#ffffff'
         }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <CalendarClock
              className="h-5 w-5 mr-2"
              style={{ color: theme.primaryColor }}
            />
            <h2 className="text-lg font-semibold">Prodotti in arrivo</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/recurring-products')}
            className="text-xs flex items-center"
            style={{
              color: theme.primaryColor,
              borderColor: theme.primaryColor
            }}
          >
            Aggiungi <Plus className="h-3 w-3 ml-1" />
          </Button>
        </div>
        <p className="text-sm " style={{
      color: theme.textColor,
    }}>
          Nessun prodotto ricorrente in arrivo
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4" style={{ backgroundColor: theme.cardBackgroundColor || '#ffffff',
        color: theme.sectionTitleColor || '#ffffff'
     }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <CalendarClock
            className="h-5 w-5 mr-2"
            style={{ color: theme.primaryColor }}
          />
          <h2 className="text-lg font-semibold">Prodotti in arrivo</h2>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/recurring-products')}
          className="text-xs flex items-center"
        >
          Vedi tutti <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>

      <div className="space-y-3 mt-2">
        {upcomingProducts.slice(0, 3).map((product) => (
          <div
            key={product.id}
            className="rounded-lg p-3 flex items-center justify-between"
            style={{
              backgroundColor: theme.cardBackgroundColor || '#ffffff',
              border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
              color: theme.sectionTitleColor || '#ffffff'
            }}
          >
            <div>
              <div className="font-medium">
                {product.product?.name}
              </div>
              <div className="text-sm" style={{ color: theme.sectionTextColor }}>
                Ogni {product.frequency_days} giorni
                {(product.effective_next_date || product.next_purchase_date) && (
                  <span className="ml-2">
                    • Prossimo:{' '}
                    {new Date(
                      product.effective_next_date || product.next_purchase_date!
                    ).toLocaleDateString()}
                  </span>
                )}
              </div>
              {product.recurrence_group && (
                <div className="text-xs mt-1" style={{ color: theme.sectionTextColor }}>
                  Gruppo: {product.recurrence_group}
                </div>
              )}
            </div>
            <Button
              size="sm"
              className=""
              style={{
                color: theme.primaryColor,
                borderColor: theme.primaryColor || '#bce4bd',
                backgroundColor: theme.cardBackgroundColor
              }}
              onClick={() => onAddToList(product.product_id)}
            >
              <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Aggiungi
            </Button>
          </div>
        ))}

        {upcomingProducts.length > 3 && (
          <div className="text-sm text-center text-gray-500">
            {upcomingProducts.length - 3} altri prodotti in scadenza
          </div>
        )}
      </div>
    </Card>
  );
};

export default UpcomingProductsSection;
